import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
}

interface SessionState {
  session: Session | null;
  profile: Profile | null;
  permissions: Set<string>;
  loading: boolean;
  /**
   * Whether the UI should offer a capability. Advisory only — the database
   * refuses the operation regardless of what this returns, so a bug here is
   * a cosmetic bug, never a security one.
   */
  can: (key: string) => boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;

  // Profile and permissions in parallel, once per sign-in. The legacy app
  // fired eight separate calls on mount; this is two, and both are cached
  // for the session.
  const profileQ = useQuery({
    queryKey: ["profile", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Profile | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,email,full_name,is_active")
        .eq("id", userId!)
        .maybeSingle();
      if (error) throw error;
      return data as Profile | null;
    },
  });

  const permsQ = useQuery({
    queryKey: ["permissions", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.rpc("my_permissions");
      if (error) throw error;
      return (data as string[] | null) ?? [];
    },
  });

  const permissions = useMemo(() => new Set(permsQ.data ?? []), [permsQ.data]);

  const value: SessionState = {
    session,
    profile: profileQ.data ?? null,
    permissions,
    loading: !ready || (!!userId && (profileQ.isLoading || permsQ.isLoading)),
    can: (key: string) => permissions.has(key),
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession must be used inside <SessionProvider>");
  return v;
}
